import axios from "./axiosInstance";
import config from "./config";

const BASE_URL = config.BASE_URL;

// Type Definitions
export type Audience = 'customer' | 'tenant_staff' | 'platform_admin';

export interface Article {
  id: string;
  title: string;
  excerpt: string | null;
  content: string | null;
  read_time: number | null;
  is_popular?: boolean;
  created_at: string;
  updated_at: string;
  tags?: string[] | null;
  author_id?: string | null;
  kb_categories: {
    id?: string;
    name: string;
    description?: string | null;
  };
}

export interface Category {
  id: string;
  name: string;
  description: string | null;
  sort_order: number;
  articleCount: number;
}

export interface FAQ {
  id: string;
  question: string;
  answer: string;
  sort_order: number;
  created_at: string;
}

export interface Announcement {
  id: string;
  title: string;
  content: string;
  date: string;
  created_at: string;
}

export interface KnowledgebaseDashboard {
  recentArticles: Article[];
  popularArticles: Article[];
  categories: Category[];
}

/**
 * Helper function to determine the user's audience based on their role
 *
 * @returns The audience type for the current user
 *
 * Audience types:
 * - 'customer': For tenant members/customers (role === 'member' or no role)
 * - 'tenant_staff': For tenant staff and administrators (role === 'admin', 'staff', 'coach', or 'tenant owner')
 * - 'platform_admin': For platform administrators (not yet fully implemented)
 */
export function getUserAudience(): Audience {
  // Get user role from localStorage (stored when user logs in, already lowercased)
  const userRole = localStorage.getItem('userRole')?.toLowerCase();

  // Check if user is platform admin (future implementation)
  // const isPlatformAdmin = localStorage.getItem('isPlatformAdmin') === 'true';
  // if (isPlatformAdmin) {
  //   return 'platform_admin';
  // }

  // Check if user is tenant staff (admin, staff, coach, tenant owner)
  if (
    userRole === 'admin' ||
    userRole === 'staff' ||
    userRole === 'coach' ||
    userRole === 'tenant owner'
  ) {
    return 'tenant_staff';
  }

  // Default to customer for members or users without a specific role
  return 'customer';
}

export interface KbRequestOptions {
  /**
   * The family Help page: ask for the family articles only (`view=family`).
   * The server decides what a caller may see; this can only narrow it, so a
   * staff member on the family page sees exactly what families see.
   */
  family?: boolean;
}

function familyParams(audience: Audience, options: KbRequestOptions) {
  return options.family ? { audience, view: "family" } : { audience };
}

/**
 * Get knowledgebase dashboard data (recent articles, popular articles, categories)
 *
 * @param token - Authentication token
 * @returns Promise with dashboard data
 */
export const getKnowledgeBase = (token: string | null, options: KbRequestOptions = {}) => {
  const audience = getUserAudience();

  return axios
    .get(`${BASE_URL}/knowledgebase`, {
      headers: {
        "Content-Type": "application/json",
        authorization: `Bearer ${token}`,
      },
      params: familyParams(audience, options),
    })
    .then((res) => {
      if (res) return res;
    })
    .catch((err) => {
      throw err;
    });
};

/**
 * Get all published knowledgebase articles for the current user's audience
 *
 * @param token - Authentication token
 * @returns Promise with array of articles
 */
export const getAllKnowledgebaseArticles = (token: string | null, options: KbRequestOptions = {}) => {
  const audience = getUserAudience();

  return axios
    .get(`${BASE_URL}/knowledgebase/all-articles`, {
      headers: {
        "Content-Type": "application/json",
        authorization: `Bearer ${token}`,
      },
      params: familyParams(audience, options),
    })
    .then((res) => {
      if (res) return res;
    })
    .catch((err) => {
      throw err;
    });
};

/**
 * Get all FAQs for the current user's audience (ordered by sort_order, then created_at)
 *
 * @param token - Authentication token
 * @returns Promise with array of FAQs
 */
export const getAllFaq = (token: string | null, options: KbRequestOptions = {}) => {
  const audience = getUserAudience();

  return axios
    .get(`${BASE_URL}/knowledgebase/faq`, {
      headers: {
        "Content-Type": "application/json",
        authorization: `Bearer ${token}`,
      },
      params: familyParams(audience, options),
    })
    .then((res) => {
      if (res) return res;
    })
    .catch((err) => {
      throw err;
    });
};

/**
 * Get updates and announcements for the current user's audience
 *
 * @param token - Authentication token
 * @returns Promise with array of announcements
 */
export const getUpdatesAndannouncements = (token: string | null) => {
  const audience = getUserAudience();

  return axios
    .get(`${BASE_URL}/knowledgebase/updates-and-announcements`, {
      headers: {
        "Content-Type": "application/json",
        authorization: `Bearer ${token}`,
      },
      params: { audience },
    })
    .then((res) => {
      if (res) return res;
    })
    .catch((err) => {
      throw err;
    });
};
